import { PopupProps } from '@mui/base/Unstable_Popup';
import React from 'react';
import css from './select.module.scss';
import { Select as SelectBase, SelectRootSlotProps } from '@mui/base/Select';
import { Option as BaseOption } from '@mui/base/Option';
import Icon from '../Icon/Icon';
import clsx from 'clsx';

type Variant = 'default' | 'inline' | 'text' | 'filter' | 'button';
type Color =
  | 'default'
  | 'black'
  | 'success'
  | 'warning'
  | 'warning-light'
  | 'error';

type Size = 'default' | 'small' | 'medium' | 'large';

export type Props = React.ComponentProps<typeof SelectBase> & {
  variant?: Variant;
  color?: Color;
  size?: Size;
  uppercase?: boolean;
  slotProps?: {
    popup?: PopupProps;
  };
};

const Button = React.forwardRef(function Button<
  TValue extends {},
  Multiple extends boolean,
>(
  props: SelectRootSlotProps<TValue, Multiple>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const { ownerState, children, ...rest } = props;

  return (
    <button type="button" className={css.button} {...rest} ref={ref}>
      <span className={css['button-label']}>{children}</span>
      <Icon className={css['arrow-button']} name={'fas fa-chevron-down'} />
    </button>
  );
});

const Option = ({
  className,
  ...props
}: React.ComponentProps<typeof BaseOption>) => (
  <BaseOption {...props} className={clsx(css.option, className)} />
);

const Select = ({
  children,
  variant = 'default',
  color = 'default',
  size = 'default',
  uppercase = false,
  className,
  slotProps: { popup = {} } = {},
  ...props
}: Props) => (
  <SelectBase
    {...props}
    className={clsx(
      css.root,
      variant && css[`variant-${variant}`],
      color && css[`color-${color}`],
      size && css[`size-${size}`],
      uppercase && css.uppercase,
      className,
    )}
    slots={{
      root: Button,
    }}
    slotProps={{
      listbox: { className: css.list },
      popup: {
        ...popup,
        className: css.popup,
        disablePortal: true,
      },
    }}
  >
    {children}
  </SelectBase>
);

export default Object.assign(Select, {
  Option,
});
